import { useMemo } from "react";

import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import { apiClient } from "app/modules/api-client";
import { MAX_SELECTED_BRANDS } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/model-comparison/consts";
import type {
    IModelComparisonBrand,
    IModelComparisonResponse,
    IModelComparisonTopic,
    SeriesData,
    SeriesItem,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/model-comparison/types";
import { EViewMode } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/model-comparison/types";
import { buildCompareCoreAverageSeries } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/shared/coreCompetitorsUtils";
import { useLLMReportQuery } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMReportQuery";
import {
    assignDynamicLegendColors,
    sortByMyBrandFirst,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    IAiTrackerCampaign,
    ILLMWidgetRequestParams,
    LLModel,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

type CompareViewMode = EViewMode.Visibility | EViewMode.Sentiment;

const COMPARE_VIEW_MODES: CompareViewMode[] = [EViewMode.Visibility, EViewMode.Sentiment];

interface UseModelComparisonGraphDataResult {
    data: IModelComparisonResponse | undefined;
    transformedData: Record<CompareViewMode, SeriesData> | undefined;
    isLoading: boolean;
    isError: boolean;
}

const isCampaignBrand = (campaign: IAiTrackerCampaign, brandName: string): boolean =>
    campaign.brands.some((brand) => brand.toLowerCase() === brandName.toLowerCase());

const getBrandAvgMetric = (brand: IModelComparisonBrand, viewMode: CompareViewMode): number =>
    brand.avg?.[viewMode] ?? 0;

const buildBrandColumnDataPoints = (
    brandModelData: IModelComparisonBrand["data"],
    availableModels: LLModel[],
    viewMode: CompareViewMode,
): number[][] =>
    availableModels.reduce((modelsAcc, model) => {
        const modelRecord = brandModelData?.find((record) => model in record);
        const modelData = modelRecord?.[model];
        const viewStatistic = modelData?.[viewMode as keyof typeof modelData] ?? 0;
        const modelIndex = availableModels.indexOf(model) ?? 0;

        modelsAcc.push([modelIndex, viewStatistic]);
        return modelsAcc;
    }, [] as number[][]);

const transformCompareTopicData = (
    topicData: IModelComparisonTopic,
    myBrandDisplayName: string,
    availableModels: LLModel[],
    viewMode: CompareViewMode,
    topicKey: string,
    onlyCoreCompetitors: boolean,
    campaign: IAiTrackerCampaign,
    isMultiBrand: boolean,
): SeriesItem[] => {
    const results: Array<SeriesItem & { isMyBrand?: boolean }> = [];

    if (topicData.myBrand) {
        results.push({
            id: `${viewMode}-${topicKey}-${myBrandDisplayName}`,
            name: myBrandDisplayName,
            color: "",
            total: getBrandAvgMetric(topicData.myBrand, viewMode),
            data: buildBrandColumnDataPoints(topicData.myBrand.data, availableModels, viewMode),
            visible: true,
            isMyBrand: true,
        });
    }

    (topicData.brands as IModelComparisonBrand[])?.forEach((brand) => {
        if (onlyCoreCompetitors && !brand.isCoreCompetitor) {
            return;
        }

        if (!brand.brandName || (!isMultiBrand && isCampaignBrand(campaign, brand.brandName))) {
            return;
        }

        results.push({
            id: `${viewMode}-${topicKey}-${brand.brandName}`,
            name: brand.brandName,
            color: "",
            total: getBrandAvgMetric(brand, viewMode),
            data: buildBrandColumnDataPoints(brand.data, availableModels, viewMode),
            visible: true,
            isMyBrand: false,
        });
    });

    return sortByMyBrandFirst(results).map((data, index) => ({ ...data, visible: index < MAX_SELECTED_BRANDS }));
};

const transformCompareData = (
    campaign: IAiTrackerCampaign,
    topics: Record<string, IModelComparisonTopic>,
    availableModels: LLModel[],
    onlyCoreCompetitors: boolean,
): Record<CompareViewMode, SeriesData> => {
    const isMultiBrand = campaign.brands.length > 1;
    const myBrandDisplayName = translate(
        isMultiBrand ? "llm.llm-comparison.graph.legend.my_brand" : "llm.llm-comparison.graph.legend.my_brand.single",
        { brand: campaign.brands[0] },
    );

    return Object.entries(topics).reduce<Record<CompareViewMode, SeriesData>>(
        (acc, [topicName, topicData]) => {
            if (!topicData.myBrand) {
                return acc;
            }

            COMPARE_VIEW_MODES.forEach((viewMode) => {
                const coloredBrands = assignDynamicLegendColors(
                    transformCompareTopicData(
                        topicData,
                        myBrandDisplayName,
                        availableModels,
                        viewMode,
                        topicName,
                        onlyCoreCompetitors,
                        campaign,
                        isMultiBrand,
                    ),
                );

                const coreAverageSeries = onlyCoreCompetitors
                    ? buildCompareCoreAverageSeries(
                          viewMode,
                          topicName,
                          topicData.coreAverage,
                          availableModels.length,
                      )
                    : null;

                acc[viewMode][topicName] = coreAverageSeries
                    ? [...coloredBrands, coreAverageSeries]
                    : coloredBrands;
            });

            return acc;
        },
        { [EViewMode.Visibility]: {}, [EViewMode.Sentiment]: {} },
    );
};

export const useModelComparisonAveragesGraphData = (
    params: ILLMWidgetRequestParams,
    campaign: IAiTrackerCampaign | undefined,
    models: LLModel[],
    onlyCoreCompetitors: boolean,
    options?: { enabled?: boolean },
): UseModelComparisonGraphDataResult => {
    const { data, isLoading, isError } = useLLMReportQuery<IModelComparisonResponse, ILLMWidgetRequestParams>(
        "modelComparison",
        apiClient.AiTracker.aiTrackerReportsBrandVisibilityCompareReportBrandMetricsGraphCompareList,
        params,
        options,
    );

    const transformedData = useMemo<Record<CompareViewMode, SeriesData> | undefined>(() => {
        if (!data?.topics || !campaign) {
            return undefined;
        }

        try {
            return transformCompareData(campaign, data.topics, models, onlyCoreCompetitors);
        } catch (error: unknown) {
            Sentry.errorTracker({
                e: error,
                message: "Error transforming LLM comparison data",
                level: "error",
                params: { extra: { moduleName: "llm-tracker", campaignId: campaign?.id } },
            });
            return undefined;
        }
    }, [data, campaign, models, onlyCoreCompetitors]);

    return {
        data,
        transformedData,
        isLoading,
        isError,
    };
};
